import React, { useState, useEffect } from 'react';
import { api } from '../hooks/useApi';
import { formatApiErrorDetail } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { KeyRound, Loader2, CheckCircle2 } from 'lucide-react';

const MIN_LENGTH = 10;
const EMPTY = { current_password: '', new_password: '', confirm: '' };

// Lets the logged-in user change their OWN password (any role, including the
// super admin, whose account cannot be edited from Settings -> Users).
const ChangePasswordDialog = ({ isOpen, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (isOpen) { setForm(EMPTY); setError(''); setDone(false); }
  }, [isOpen]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.new_password.length < MIN_LENGTH) {
      setError(`New password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (form.new_password !== form.confirm) {
      setError('The two new passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || 'Could not change the password');
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, testid, autoComplete) => (
    <div className="space-y-2">
      <Label className="text-[#94A3B8] text-sm">{label}</Label>
      <Input
        type="password"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        required
        autoComplete={autoComplete}
        className="bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]"
        data-testid={testid}
      />
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-[#3B82F6]" /> Change Password</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            Changes the password for your own login.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-emerald-400 bg-emerald-900/20 border border-emerald-800 rounded-md p-3" data-testid="change-password-success">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Password changed. Use the new password next time you log in.
            </div>
            <div className="flex justify-end">
              <Button type="button" onClick={onClose} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="change-password-close-btn">Done</Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {field('current_password', 'Current password', 'current-password-input', 'current-password')}
            {field('new_password', `New password (at least ${MIN_LENGTH} characters)`, 'new-password-input', 'new-password')}
            {field('confirm', 'New password again', 'confirm-password-input', 'new-password')}
            {error && (
              <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="change-password-error">{error}</div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] text-[#94A3B8]">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="submit-change-password-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Change Password'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordDialog;
